// ============================================================
//  AI 助手卡片：宽松类型 + 解析/守卫（无框架、无副作用）
//  契约：.scratch/ai-chat-assistant/spec.md §八（信封 cards）
//    · contact ：字段卡（电话类可拨号）——**PII 原值只出现在这里**
//    · record  ：记录列表卡（domain + rows；行键见 spec §6.2 行键契约表）
//    · students：消歧候选卡（可点选补学号追问）
//    · image   ：生图卡（AI 生图，见 .scratch/ai-image-generation/spec.md §5.1）
//    · examReport：成绩单卡（一名学生 × 一场考试 + 总分，可一键分享；
//                  见 .scratch/ai-score-share/spec.md 与 docs/adr/017-unsynced-score-share.md）
//  未知 type / 结构不符的卡片一律丢弃：后端加新卡片类型不会炸前端页面。
// ============================================================

/** 卡片类型常量（与后端 `AiChatCard.type` 对齐） */
export const AI_CHAT_CARD_CONTACT = "contact";
export const AI_CHAT_CARD_RECORD = "record";
export const AI_CHAT_CARD_STUDENTS = "students";
export const AI_CHAT_CARD_IMAGE = "image";
export const AI_CHAT_CARD_EXAM_REPORT = "examReport";

/** phone 类字段 key（前端据此渲染拨号动作；与后端 T2 白名单一致） */
export const AI_CHAT_PHONE_FIELD_KEYS: readonly string[] = [
  "fatherPhone",
  "motherPhone",
  "dailyContactPhone",
];

/** 字段卡的一行 */
export interface AiChatCardField {
  key: string;
  label: string;
  value: string;
}

/** 消歧候选行 */
export interface AiChatStudentRow {
  id: number;
  name: string;
  className: string;
  studentNo: string;
}

/**
 * 成绩单卡的一个科目。
 * 注意分数是**数字**（record 卡的行是无类型字符串，两者不同，勿互相套用）。
 */
export interface AiChatExamReportSubject {
  subject: string;
  score: number;
  /** 满分（来自考试科目配置；后端缺失或 ≤0 时兜底 100） */
  fullScore?: number;
}

/** 结构化卡片（宽松形状：按 type 使用对应字段） */
export interface AiChatCard {
  type: string;
  studentId?: number;
  studentName?: string;
  studentNo?: string;
  fields?: AiChatCardField[];
  domain?: string;
  title?: string;
  rows?: Array<Record<string, string>>;
  students?: AiChatStudentRow[];
  /** image 卡：图片地址（GRS 返回，约 2 天有效；见 ai-image-generation spec §3.4） */
  imageUrl?: string;
  /** image 卡：本次生成用的提示词（供教师确认/微调复用） */
  imagePrompt?: string;
  /** image 卡：模型标识，如 gpt-image-2 */
  imageModel?: string;
  /** image 卡：宽高比，如 "1:1" / "16:9" */
  aspectRatio?: string;
  /** examReport 卡：考试名称 */
  examName?: string;
  /** examReport 卡：考试日期 yyyy-MM-dd */
  examDate?: string;
  /** examReport 卡：阶段/类型（空则不展示） */
  stage?: string;
  /** examReport 卡：年级 / 班级（取学生档案真值；缺省时不展示） */
  grade?: string;
  className?: string;
  /** examReport 卡：各科成绩（按科目顺序） */
  subjects?: AiChatExamReportSubject[];
  /** examReport 卡：该场考试的总分——由后端按完整成绩行求和，前端不得自行相加 */
  totalScore?: number;
}

/** 该字段是否电话类（渲染拨号按钮 + 复制） */
export function isPhoneFieldKey(key: string): boolean {
  return AI_CHAT_PHONE_FIELD_KEYS.includes(key);
}

/** 宽松解析后端 cards 数组：逐张校验，未知/残缺卡片丢弃 */
export function parseCards(raw: unknown): AiChatCard[] {
  if (!Array.isArray(raw)) return [];
  const cards: AiChatCard[] = [];
  for (const item of raw) {
    const card = parseCard(item);
    if (card) cards.push(card);
  }
  return cards;
}

function parseCard(raw: unknown): AiChatCard | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const type = text(o["type"]);
  if (type === AI_CHAT_CARD_CONTACT) return parseContactCard(o);
  if (type === AI_CHAT_CARD_RECORD) return parseRecordCard(o);
  if (type === AI_CHAT_CARD_STUDENTS) return parseStudentsCard(o);
  if (type === AI_CHAT_CARD_IMAGE) return parseImageCard(o);
  if (type === AI_CHAT_CARD_EXAM_REPORT) return parseExamReportCard(o);
  return null;
}

function parseContactCard(o: Record<string, unknown>): AiChatCard | null {
  const fields = parseFields(o["fields"]);
  const studentName = text(o["studentName"]);
  const studentNo = text(o["studentNo"]);
  const studentId = positiveInt(o["studentId"]);
  // 既无字段又无姓名：信息为空，丢弃（避免渲染空卡片）
  if (fields.length === 0 && studentName.length === 0) return null;
  return { type: AI_CHAT_CARD_CONTACT, studentId, studentName, studentNo, fields };
}

function parseRecordCard(o: Record<string, unknown>): AiChatCard | null {
  const domain = text(o["domain"]);
  const rows = parseRows(o["rows"]);
  if (domain.length === 0 || rows.length === 0) return null;
  return {
    type: AI_CHAT_CARD_RECORD,
    domain,
    title: text(o["title"]),
    studentName: text(o["studentName"]),
    rows,
  };
}

function parseStudentsCard(o: Record<string, unknown>): AiChatCard | null {
  const students = parseStudentRows(o["students"]);
  if (students.length === 0) return null;
  return { type: AI_CHAT_CARD_STUDENTS, title: text(o["title"]), students };
}

/**
 * 生图卡：**图片地址是这张卡的全部价值**——拿不到有效地址就丢弃，
 * 免得对话里留一个空的破图位（后端失败时本就不该发卡，这里是二道防线）。
 */
function parseImageCard(o: Record<string, unknown>): AiChatCard | null {
  const imageUrl = safeImageUrl(o["imageUrl"]);
  if (imageUrl.length === 0) return null;
  return {
    type: AI_CHAT_CARD_IMAGE,
    imageUrl,
    imagePrompt: text(o["imagePrompt"]),
    imageModel: text(o["imageModel"]),
    aspectRatio: text(o["aspectRatio"]),
  };
}

/**
 * 成绩单卡：**各科成绩是这张卡的全部价值**——没有科目、或总分不是有限数就丢弃，
 * 免得对话里留一张"发不出去"的空成绩单（同 parseImageCard 的思路）。
 *
 * ⚠️ 总分缺失**不能**退化为前端求和：T7 有行数上限，被截断后的分数之和会偏小，
 * 而它是**要发给家长**的数字。宁可整张卡不出现（见 spec 硬约束 1）。
 */
function parseExamReportCard(o: Record<string, unknown>): AiChatCard | null {
  const examName = text(o["examName"]);
  if (examName.length === 0) return null;
  const subjects = parseExamSubjects(o["subjects"]);
  if (subjects.length === 0) return null;
  const totalScore = finiteNumber(o["totalScore"]);
  if (totalScore === undefined) return null;
  return {
    type: AI_CHAT_CARD_EXAM_REPORT,
    examName,
    examDate: text(o["examDate"]),
    stage: text(o["stage"]),
    studentName: text(o["studentName"]),
    grade: text(o["grade"]),
    className: text(o["className"]),
    subjects,
    totalScore,
  };
}

/** 成绩单卡的科目行：科目名为空或分数非有限数的一律丢弃（单科坏数据不该拖垮整张卡） */
function parseExamSubjects(raw: unknown): AiChatExamReportSubject[] {
  if (!Array.isArray(raw)) return [];
  const subjects: AiChatExamReportSubject[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    const subject = text(o["subject"]);
    const score = finiteNumber(o["score"]);
    if (subject.length === 0 || score === undefined) continue;
    subjects.push({ subject, score, fullScore: finiteNumber(o["fullScore"]) });
  }
  return subjects;
}

/**
 * 图片地址白名单：只放行 http(s) 绝对地址与站内根相对路径（为后续「转存到本站」留位）。
 * 地址来自外部生图服务（GRS）经后端转发，`javascript:` 之类一律拒绝。
 */
function safeImageUrl(raw: unknown): string {
  const value = text(raw);
  if (value.length === 0) return "";
  if (value.startsWith("/") && !value.startsWith("//")) return value;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:" ? value : "";
  } catch {
    return "";
  }
}

function parseFields(raw: unknown): AiChatCardField[] {
  if (!Array.isArray(raw)) return [];
  const fields: AiChatCardField[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    const key = text(o["key"]);
    const value = text(o["value"]);
    if (key.length === 0 || value.length === 0) continue;
    const label = text(o["label"]);
    fields.push({ key, label: label.length > 0 ? label : key, value });
  }
  return fields;
}

function parseRows(raw: unknown): Array<Record<string, string>> {
  if (!Array.isArray(raw)) return [];
  const rows: Array<Record<string, string>> = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const source = item as Record<string, unknown>;
    const row: Record<string, string> = {};
    for (const key of Object.keys(source)) {
      row[key] = text(source[key]);
    }
    // 全空行无意义，丢弃
    if (Object.values(row).some((value) => value.length > 0)) rows.push(row);
  }
  return rows;
}

function parseStudentRows(raw: unknown): AiChatStudentRow[] {
  if (!Array.isArray(raw)) return [];
  const rows: AiChatStudentRow[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    const id = positiveInt(o["id"]);
    const name = text(o["name"]);
    if (!id || name.length === 0) continue;
    rows.push({
      id,
      name,
      className: text(o["className"]),
      studentNo: text(o["studentNo"]),
    });
  }
  return rows;
}

/** 归一为去空白字符串（非字符串类型 → 空串） */
function text(raw: unknown): string {
  return typeof raw === "string" ? raw.trim() : "";
}

function positiveInt(raw: unknown): number | undefined {
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

/**
 * 归一为有限数字；`null` / `undefined` / 空串一律 undefined。
 *
 * 为什么不直接 `Number(raw)`：`Number(null)` 与 `Number("")` 都是 **0**，
 * 会让"缺失的总分"变成一个看着很真的「总分 0」——而它是发给家长的数字。
 */
function finiteNumber(raw: unknown): number | undefined {
  if (raw === null || raw === undefined) return undefined;
  if (typeof raw === "string" && raw.trim().length === 0) return undefined;
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
}
